import { DisplayElementModel } from './display-element.model';
import { MonitoringDateStyleModel } from '../../../Monitor/Shared/models/monitoring-date-style-model';
import { DateQueryModel } from '../../Forms/models/date-query.model';
import { BriefingDateRangeTypeEnum } from '../../../Monitor/Briefings/enums/briefing-date-range-type.enum';

export class DateDisplayElementModel extends DisplayElementModel {
    declare styles: MonitoringDateStyleModel;
    date: number;
    format: string;
    dateQuery?: DateQueryModel;
    // In "Date Range" mode, whether the range is a specific selection (`dateQuery`) or
    // derived from the Briefing's recurrence schedule. Undefined for legacy/non-range blocks.
    dateRangeType?: BriefingDateRangeTypeEnum;
    // Prevents rendered links from receiving focus/ being activated via keyboard
    preventChildFocus: boolean = true;

    /**
     * Class Constructor
     */
    constructor(date?: number, format?: string, styles?: any) {
        super();

        this.date = date ? date : null;
        this.format = format ? format : 'F d, Y';
        this.styles = styles && styles.clone ? styles.clone() : new MonitoringDateStyleModel(styles);
        this.isBoundToGlobalStyles = true;
    }

    /**
     * True if the component contains the information it needs to render a preview
     */
    canRender(): boolean {
        return !!this.format;
    }

    /**
     * Deep copy of the model
     */
    clone(): DateDisplayElementModel {
        // HACK for alerts, somewhere in alerts this.styles isn't an object
        const clone = new DateDisplayElementModel(
            this.date ? this.date : null,
            this.format,
            this.styles && this.styles.clone ? this.styles.clone() : JSON.parse(JSON.stringify(this.styles))
        );

        clone.displayType = this.displayType;
        clone.dateQuery = this.dateQuery ? DateQueryModel.clone(this.dateQuery) : undefined;
        clone.dateRangeType = this.dateRangeType;

        return clone;
    }

    /**
     * Implemented to meet requirements of interface, returns true
     */
    onUpdate(): boolean {
        return true;
    }

    isEqual(other: DateDisplayElementModel): boolean {
        // Have to ensure that both values are Date objects before comparison.
        const thisDate: Date = this.date ? new Date(this.date) : new Date(0);
        const otherDate: Date = new Date(other.date);
        if (thisDate.getTime() !== otherDate.getTime()) {
            return false;
        }

        if (this.format !== other.format) {
            return false;
        }

        // Detect changes to the date range query (e.g. switching between published time and
        // a date range, picking a different relative/absolute range, or changing the timezone),
        // so the briefing goes dirty and offers "Save as Draft".
        if (!DateQueryModel.equals(this.dateQuery, other.dateQuery, true, true)) {
            return false;
        }

        // Switching between "Specific Date Range" and "Based on Recurrence Date Range"
        // must mark the content dirty so it offers "Save as Draft".
        if ((this.dateRangeType ?? null) !== (other.dateRangeType ?? null)) {
            return false;
        }

        if (!this.styles.equals(other.styles, true)) {
            return false;
        }

        if (this.isBoundToGlobalStyles !== other.isBoundToGlobalStyles) {
            return false;
        }
        return true;
    }
}
